import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Inbox, ArrowRight } from "lucide-react";
import { mailboxApi } from "@/lib/api";

const SERIES = [
  { key: "support", label: "Support", match: /support/i, color: "#E8700A" },
  { key: "updates", label: "Updates", match: /update|release/i, color: "#1E3A5F" },
  { key: "invoices", label: "Rechnungen", match: /rechnung/i, color: "#16A34A" },
];
const fmtWeek = (iso) => new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
const isoWeek = (iso) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  return Math.ceil(((d - new Date(Date.UTC(d.getUTCFullYear(), 0, 1))) / 864e5 + 1) / 7);
};

const toRows = ({ weeks, topics }) => {
  const ids = Object.fromEntries(SERIES.map((s) => [s.key, topics.filter((t) => s.match.test(t.name)).map((t) => t.id)]));
  return weeks.map((w) => {
    const row = { week: `KW ab ${fmtWeek(w.week_start)}` };
    SERIES.forEach((s) => { row[s.key] = ids[s.key].reduce((n, id) => n + (w.counts[id] || 0), 0); });
    row.week = `KW ${isoWeek(w.week_start)}`;
    row.range = `ab ${fmtWeek(w.week_start)}`;
    return row;
  });
};

export function MailTopicsWeeklyCard() {
  const [rows, setRows] = useState(null);
  useEffect(() => { mailboxApi.weeklyStats(8).then((r) => setRows(toRows(r.data))).catch(() => setRows([])); }, []);
  const totals = SERIES.map((s) => ({ ...s, sum: (rows || []).reduce((n, r) => n + r[s.key], 0) }));

  return (
    <Card data-testid="dashboard-mail-topics-widget">
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base"><Inbox className="w-4 h-4 text-primary" />Service-Postfach nach Thema</CardTitle>
          <CardDescription>Eingegangene Mails pro Woche · letzte 8 Wochen</CardDescription>
        </div>
        <Button variant="ghost" size="sm" asChild><Link to="/mailbox" data-testid="dashboard-mail-topics-link">Postfach<ArrowRight className="w-4 h-4 ml-1" /></Link></Button>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 mb-4">
          {totals.map((s) => (
            <div key={s.key} className="flex items-center gap-2 text-xs rounded-lg border px-3 py-1.5" style={{ background: `${s.color}0D`, borderColor: `${s.color}33` }} data-testid={`mail-topics-total-${s.key}`}>
              <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
              <span className="text-slate-600">{s.label}</span>
              <span className="font-bold tabular-nums" style={{ color: s.color }}>{rows ? s.sum : "…"}</span>
            </div>
          ))}
        </div>
        <div className="h-64 sm:h-72" data-testid="mail-topics-chart">
          {rows && (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 4, right: 8, left: -16, bottom: 0 }} barGap={3}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} />
                <Tooltip labelFormatter={(l, p) => `${l} · ${p?.[0]?.payload?.range || ""}`} cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 12, fontSize: 12, border: "1px solid #E2E8F0", boxShadow: "0 10px 25px -5px rgba(15,23,42,.12)" }} />
                {SERIES.map((s) => <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[4, 4, 0, 0]} maxBarSize={20} />)}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
